// ========================================================
// FAST HIT Website — FAQ Section Component
// Accordion FAQ with category filtering and keyboard accessibility
// ========================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { getFaqList, type FaqItem } from '../../services/cmsService';
import './FaqSection.css';

type CategoryFilter = 'all' | 'general' | 'scoring' | 'tournaments' | 'account';

const CATEGORY_TABS: { key: CategoryFilter; label: string }[] = [
  { key: 'all', label: 'All Questions' },
  { key: 'general', label: 'General' },
  { key: 'scoring', label: 'Match Scoring' },
  { key: 'tournaments', label: 'Tournaments' },
  { key: 'account', label: 'Player Accounts' },
];

export function FaqSection() {
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set(['faq-1', 'faq-2']));

  const { data: faqs = [] } = useQuery({
    queryKey: ['faqList'],
    queryFn: getFaqList,
  });

  const toggleFaq = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filteredFaqs =
    activeCategory === 'all'
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  return (
    <section className="faq-sec" aria-labelledby="faq-title">
      <div className="faq-sec__container">
        {/* Header */}
        <div className="faq-sec__header text-center">
          <div className="section-eyebrow">
            <HelpCircle size={14} className="text-cyan" />
            <span>Got Questions?</span>
          </div>
          <h2 id="faq-title" className="section-title">
            Frequently Asked Questions
          </h2>
          <p className="section-subtitle mx-auto">
            Everything you need to know about FAST HIT live scores, scoring rules, tournaments, and accounts.
          </p>

          {/* Category Filter Tabs */}
          <div className="faq-sec__tabs" role="tablist" aria-label="FAQ categories">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={activeCategory === tab.key}
                className={`faq-sec__tab ${activeCategory === tab.key ? 'faq-sec__tab--active' : ''}`}
                onClick={() => setActiveCategory(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Accordion List */}
        <div className="faq-sec__list">
          {filteredFaqs.map((faq) => {
            const isOpen = openIds.has(faq.id);
            return (
              <div
                key={faq.id}
                className={`faq-item card ${isOpen ? 'faq-item--open' : ''}`}
              >
                <button
                  type="button"
                  className="faq-item__trigger"
                  onClick={() => toggleFaq(faq.id)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${faq.id}`}
                >
                  <span className="faq-item__question">{faq.question}</span>
                  <ChevronDown
                    size={18}
                    className={`faq-item__icon ${isOpen ? 'faq-item__icon--rotated' : ''}`}
                  />
                </button>

                {isOpen && (
                  <div
                    id={`faq-answer-${faq.id}`}
                    className="faq-item__answer"
                    role="region"
                  >
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
